import React from "react";
import { motion } from "framer-motion";
import { useLanguage } from "../../context/LanguageContext";

interface DivisionServicesProps {
  content?: any;
}

const DivisionServices: React.FC<DivisionServicesProps> = ({ content }) => {
  const { t_api } = useLanguage();

  const defaults = [
    {
      image: "/banner/banner.jpg",
      title: "Quality Services",
      title_ar: "خدمات عالية الجودة",
      desc: "Excellence in service delivery and operations.",
      desc_ar: "التميز في تقديم الخدمات والعمليات.",
    },
    {
      image: "/banner/banner2.jpg",
      title: "Safety & Standards",
      title_ar: "السلامة والمعايير",
      desc: "Adhering to safety compliance and international regulations.",
      desc_ar: "الالتزام بمعايير السلامة واللوائح الدولية.",
    },
    {
      image: "/banner/banner4.jpg",
      title: "Customer Focus",
      title_ar: "التركيز على العملاء",
      desc: "Focused on delivering client satisfaction and building long term relations.",
      desc_ar: "التركيز على تحقيق رضا العملاء وبناء علاقات طويلة الأمد.",
    },
  ];

  const data = content || {};

  // 1. Check if there are any service card keys in the database for this section
  const hasAnySvcKey = Object.keys(data).some(key => key.startsWith('svc_'));

  const services: { image: string; title: string; desc: string }[] = [];

  if (!hasAnySvcKey) {
    defaults.forEach(def => {
      services.push({
        image: def.image,
        title: t_api(def.title, def.title_ar),
        desc: t_api(def.desc, def.desc_ar),
      });
    });
  } else {
    // Find the max index
    let maxIdx = 1;
    Object.keys(data).forEach(key => {
      const match = key.match(/^svc_(\d+)_/);
      if (match) {
        const idx = parseInt(match[1]);
        if (idx > maxIdx) maxIdx = idx;
      }
    });

    for (let i = 1; i <= maxIdx; i++) {
      const hasTitle = data[`svc_${i}_title`] !== undefined;
      const hasDesc = data[`svc_${i}_desc`] !== undefined;
      const hasImage = data[`svc_${i}_image`] !== undefined;

      if (hasTitle || hasDesc || hasImage) {
        services.push({
          image: data[`svc_${i}_image`] || "",
          title: t_api(data[`svc_${i}_title`] || "", data[`svc_${i}_title_ar`] || ""),
          desc: t_api(data[`svc_${i}_desc`] || "", data[`svc_${i}_desc_ar`] || ""),
        });
      }
    }
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 font-sans">
      <motion.div
        className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-slate-100"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h2 className="text-2xl md:text-4xl font-black text-center text-slate-800 tracking-tight mb-12">
          {t_api(content?.services_title || "Our Services", content?.services_title_ar || "خدماتنا")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((svc, i) => (
            <div 
              key={i} 
              className="flex flex-col rounded-2xl bg-slate-50 border border-slate-100 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
            >
              {svc.image && (
                <div className="w-full h-56 overflow-hidden bg-slate-100 relative">
                  <img 
                    src={svc.image} 
                    alt={svc.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" 
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              )}
              <div className="p-6 md:p-8 flex flex-col flex-1">
                <h3 className="text-xl font-bold text-slate-800 mb-4 tracking-tight group-hover:text-blue-600 transition-colors">
                  {svc.title}
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed flex-1">
                  {svc.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default DivisionServices;
